import { isAttendeeItem, isSchedulingSelectionItem } from './convertToCalendarEvent';
import { CalendarDayContent } from 'owa-calendar-surface-core';
import { getGuid } from 'owa-guid';
import * as React from 'react';
import {
    CalendarSurfaceStore,
    CalendarSurfaceTransformProps,
    ColumnSelectionBehavior,
    ColumnItemsProvider,
    SurfaceColumnItemsProvider,
    allDayPanel,
    createGridLines,
    createHeaders,
    createColumnsWithContent,
    createConflictGroupsLayer,
    createItemsLayer,
    transformAllDayItem,
} from 'owa-calendar-surface-dategrid';
import { CalendarEvent } from 'owa-calendar-types';

/**
 * Creates content for mounting in calendar day (week) view
 * @param store the calendar surface store
 * @param config the configuration for calendar day/week view
 * @returns structured content for day/week view
 */
export function createSchedulingGridContent(
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps
): CalendarDayContent {
    const columnProvider = config.columnProvider;
    let isColumnItem = (item: CalendarEvent) =>
        !isSchedulingSelectionItem(item) && !isAttendeeItem(item);

    // never show declined items in scheduling grid
    let columnItemsProvider = new SurfaceColumnItemsProvider(store, columnProvider, () =>
        store.calendarItems.filter(item => isColumnItem(item) && item.ResponseType !== 'Decline')
    );

    let freeBusyItemsProvider = new SurfaceColumnItemsProvider(store, columnProvider, () =>
        store.calendarItems.filter(item => !isColumnItem(item) && item.ResponseType !== 'Decline')
    );

    let allDayProvider = new SurfaceColumnItemsProvider(
        store,
        columnProvider,
        () => store.calendarItems
    );

    return {
        gridLines: () => createGridLines(store, config),
        headers: () => createHeaders(store, config),
        content: () =>
            createCalendarInDayContent(columnItemsProvider, freeBusyItemsProvider, store, config),
        allDayContent: () => createAllDayPanel(allDayProvider, store, config),
        tabOrderProvider: () => [...columnItemsProvider.tabOrder, 'schedulingSelection'],
        initialTabId: 'schedulingSelection',
    };
}

/**
 * Creates the calendar surface content
 * @param items surface items collection
 * @param allDayModels collection of all day items
 * @param store surface store
 * @param config the configuration for calendar day/week view
 * @returns collection JSX elements of surface items
 */
export function createCalendarInDayContent(
    cip: ColumnItemsProvider,
    fbp: ColumnItemsProvider,
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps
): JSX.Element[] {
    let columnElements = createColumnsWithContent(
        config.columnProvider,
        (columnModel, index) => {
            return [
                ...createConflictGroupsLayer(
                    cip.columnInDayItems[index],
                    store,
                    config,
                    index,
                    element => element
                ),
                ...createItemsLayer(fbp.columnInDayItems[index], store, config, index),
            ];
        },
        (columnModel, index) => {
            return [
                <ColumnSelectionBehavior
                    key="selection"
                    surfaceStore={store}
                    config={config}
                    columnIndex={index}
                />,
            ];
        }
    );

    return columnElements;
}

/**
 * Creates all day panel with content
 * @param cip The column items provider
 * @param store The calendar surface store
 * @param config The surface transform config
 */
export function createAllDayPanel(
    cip: ColumnItemsProvider,
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps
): JSX.Element[] {
    return [
        allDayPanel(getGuid(), cip, () => {
            return createCalendarAllDayContent(cip, store, config);
        }),
    ];
}

/**
 * Creates the content (JSX elements) for the all day well
 * @param cip The column items provider
 * @param store The surface store
 * @param config the configuration for calendar day/week view
 * @returns An array of JSX elements with all components for the all-day well
 */
function createCalendarAllDayContent(
    cip: ColumnItemsProvider,
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps
): JSX.Element[] {
    let result: JSX.Element[] = [];

    cip.allDayRows.forEach((rowItems, rowIndex) => {
        result.push(
            ...rowItems.map(item => {
                return transformAllDayItem(
                    store,
                    config.columnProvider,
                    config.getItemConfig(item.model),
                    item,
                    rowIndex
                );
            })
        );
    });

    return result;
}
